import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  CheckCircle, 
  Clock, 
  Calendar,
  ArrowRight,
  Inbox,
  Loader2
} from 'lucide-react'
import { clsx } from 'clsx'
import { format } from 'date-fns'
import { zhCN } from 'date-fns/locale'
import DatePicker from './DatePicker'
import type { Action, ActionGrade } from '@/types'

// 等级配置
const GRADE_CONFIG: Record<ActionGrade, {
  label: string
  color: string
  bgColor: string
  borderColor: string
}> = {
  A: {
    label: 'A级',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-200',
  },
  B: {
    label: 'B级',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-200',
  },
  C: {
    label: 'C级',
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
    borderColor: 'border-orange-200',
  },
}

interface TodoItem {
  action: Action
  hypothesisTitle?: string
}

interface TodoListPanelProps {
  todos: TodoItem[]
  isLoading?: boolean
  onComplete: (actionId: string) => Promise<void>
  onUpdateScheduledDate: (actionId: string, date: string | undefined) => Promise<void>
  onNavigateToActions: () => void
  maxDisplay?: number
}

export default function TodoListPanel({
  todos,
  isLoading = false,
  onComplete,
  onUpdateScheduledDate,
  onNavigateToActions,
  maxDisplay = 3,
}: TodoListPanelProps) {
  const [completingId, setCompletingId] = useState<string | null>(null)
  const [updatingDateId, setUpdatingDateId] = useState<string | null>(null)

  const displayTodos = todos.slice(0, maxDisplay)
  const hasMore = todos.length > maxDisplay

  const handleComplete = async (actionId: string) => {
    setCompletingId(actionId)
    try {
      await onComplete(actionId)
    } finally {
      setCompletingId(null)
    }
  }

  const handleUpdateDate = async (actionId: string, date: string | undefined) => {
    setUpdatingDateId(actionId)
    try {
      await onUpdateScheduledDate(actionId, date)
    } finally {
      setUpdatingDateId(null)
    }
  }

  const formatScheduledDate = (dateStr?: string) => {
    if (!dateStr) return null
    const date = new Date(dateStr)
    return format(date, 'M月d日 (E)', { locale: zhCN })
  }

  // 加载状态
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-gray-200 rounded animate-pulse" />
            <div className="h-5 w-24 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>
        <div className="p-5 space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="p-4 border border-gray-100 rounded-lg animate-pulse">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-5 bg-gray-200 rounded-full" />
                <div className="h-4 w-3/4 bg-gray-200 rounded" />
              </div>
              <div className="h-3 w-1/2 bg-gray-100 rounded mt-2" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      {/* 头部 */}
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-lg">📋</span>
          <h3 className="font-semibold text-gray-900">待办事项</h3>
          {todos.length > 0 && (
            <span className="text-xs px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full">
              {todos.length}
            </span>
          )}
        </div>
        {todos.length > 0 && (
          <button
            onClick={onNavigateToActions}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-primary-600 transition-colors"
          >
            查看全部
            <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* 待办列表 */}
      <div className="p-5">
        {todos.length === 0 ? (
          // 空状态
          <div className="text-center py-8">
            <Inbox className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 mb-1">暂无待办事项</p>
            <p className="text-sm text-gray-400 mb-4">
              前往"建议动作"页面采纳动作后将在此显示
            </p>
            <button
              onClick={onNavigateToActions}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-primary-50 text-primary-700 hover:bg-primary-100 rounded-lg transition-colors"
            >
              前往建议动作
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <AnimatePresence mode="popLayout">
              {displayTodos.map((todo, index) => {
                const gradeConfig = GRADE_CONFIG[todo.action.actionGrade] || GRADE_CONFIG.B
                const isCompleting = completingId === todo.action.id
                const isUpdatingDate = updatingDateId === todo.action.id

                return (
                  <motion.div
                    key={todo.action.id}
                    layout
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20, height: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={clsx(
                      'p-4 rounded-lg border-2 transition-all',
                      gradeConfig.borderColor,
                      'hover:shadow-sm'
                    )}
                  >
                    {/* 头部：等级 + 内容 */}
                    <div className="flex items-start gap-3">
                      <span className={clsx(
                        'flex-shrink-0 text-xs font-bold px-2 py-0.5 rounded-full',
                        gradeConfig.bgColor,
                        gradeConfig.color
                      )}>
                        {gradeConfig.label}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-800 leading-relaxed line-clamp-2">
                          {todo.action.content}
                        </p>
                        {(todo.hypothesisTitle || todo.action.hypothesisTitle) && (
                          <p className="text-xs text-gray-400 mt-1">
                            来源: {todo.hypothesisTitle || todo.action.hypothesisTitle}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* 中部：日期和耗时 */}
                    <div className="flex items-center gap-4 mt-3 text-sm">
                      <div className="flex items-center gap-1.5 text-gray-500">
                        <Calendar className="w-4 h-4" />
                        {todo.action.scheduledDate ? (
                          <span className="text-gray-700 font-medium">
                            {formatScheduledDate(todo.action.scheduledDate)}
                          </span>
                        ) : (
                          <span className="text-gray-400">未设置日期</span>
                        )}
                      </div>
                      {todo.action.timeCostDisplay && (
                        <div className="flex items-center gap-1.5 text-gray-400">
                          <Clock className="w-4 h-4" />
                          <span>{todo.action.timeCostDisplay}</span>
                        </div>
                      )}
                    </div>

                    {/* 底部：操作按钮 */}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                      <button
                        onClick={() => handleComplete(todo.action.id)}
                        disabled={isCompleting}
                        className={clsx(
                          'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors',
                          'bg-green-100 text-green-700 hover:bg-green-200',
                          isCompleting && 'opacity-50 cursor-not-allowed'
                        )}
                      >
                        {isCompleting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <CheckCircle className="w-4 h-4" />
                        )}
                        标记完成
                      </button>

                      <div className={clsx(isUpdatingDate && 'opacity-50')}>
                        <DatePicker
                          value={todo.action.scheduledDate}
                          onChange={(date) => handleUpdateDate(todo.action.id, date)}
                          placeholder={todo.action.scheduledDate ? '修改日期' : '设置日期'}
                          disabled={isUpdatingDate}
                        />
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </AnimatePresence>

            {/* 查看更多 */}
            {hasMore && (
              <button
                onClick={onNavigateToActions}
                className="w-full py-2.5 text-sm text-gray-500 hover:text-primary-600 hover:bg-gray-50 rounded-lg transition-colors"
              >
                还有 {todos.length - maxDisplay} 个待办，点击查看全部
              </button>
            )}

            {/* 底部提示 */}
            {!hasMore && todos.length > 0 && (
              <div className="text-center pt-2">
                <button
                  onClick={onNavigateToActions}
                  className="text-sm text-gray-400 hover:text-primary-600 transition-colors"
                >
                  前往建议动作采纳更多 →
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * TodoListPanel 骨架屏
 */
export function TodoListPanelSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 bg-gray-200 rounded animate-pulse" />
          <div className="h-5 w-24 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
      <div className="p-5 space-y-3">
        {[1, 2].map((i) => (
          <div key={i} className="p-4 border border-gray-100 rounded-lg animate-pulse">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-5 bg-gray-200 rounded-full" />
              <div className="h-4 w-3/4 bg-gray-200 rounded" />
            </div>
            <div className="h-3 w-1/2 bg-gray-100 rounded mt-2" />
          </div>
        ))}
      </div>
    </div>
  )
}
